import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  ReactNode,
} from 'react';
import { DeepLinkParams } from 'utils/deepLinkHandler';

interface DeepLinkContextType {
  params: DeepLinkParams | null;
  isProcessing: boolean;
  error: string | null;
  setParams: (params: DeepLinkParams | null) => void;
  setProcessing: (processing: boolean) => void;
  setError: (error: string | null) => void;
  clearDeepLink: () => void;
}

const DeepLinkContext = createContext<DeepLinkContextType | undefined>(
  undefined,
);

export const useDeepLink = (): DeepLinkContextType => {
  const context = useContext(DeepLinkContext);
  if (!context) {
    throw new Error('useDeepLink must be used within a DeepLinkProvider');
  }
  return context;
};

interface DeepLinkProviderProps {
  children: ReactNode;
}

export const DeepLinkProvider: React.FC<DeepLinkProviderProps> = ({
  children,
}) => {
  const [params, setParamsState] = useState<DeepLinkParams | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [error, setErrorState] = useState<string | null>(null);

  const setParams = useCallback((newParams: DeepLinkParams | null) => {
    setParamsState(newParams);
    if (newParams) {
      setErrorState(null); // Clear error when new params are set
    }
  }, []);

  const setProcessing = useCallback((processing: boolean) => {
    setIsProcessing(processing);
  }, []);

  const setError = useCallback((errorMessage: string | null) => {
    setErrorState(errorMessage);
    if (errorMessage) {
      setIsProcessing(false); // Stop processing on error
    }
  }, []);

  const clearDeepLink = useCallback(() => {
    setParamsState(null);
    setIsProcessing(false);
    setErrorState(null);
  }, []);

  const contextValue: DeepLinkContextType = {
    params,
    isProcessing,
    error,
    setParams,
    setProcessing,
    setError,
    clearDeepLink,
  };

  return (
    <DeepLinkContext.Provider value={contextValue}>
      {children}
    </DeepLinkContext.Provider>
  );
};
